import { getStudioPlanRank } from 'src/routes/settings/plans/components/studio/studioFeatureGating';
import type { SellableStudioPlanId } from 'src/routes/settings/plans/components/studio/studioPlanFeatures';
import type {
  StudioAppUsageResponse,
  StudioPlan,
  StudioPlanId,
  StudioPlanTier,
} from 'src/services/api/studioCreditApi';

/**
 * The app-allowance rules the client still owns. The allowance, the current
 * count and the per-app fee are all the server's (`/studio-app-usage`,
 * `/studio-extra-apps-preview`) — the same count billing meters against — so
 * what is left here is the upgrade ladder, which has no server equivalent.
 *
 * Kept free of React, redux and API imports so it stays unit-testable on its
 * own — `usePlanStatus` reaches `src/utils` and therefore `axios`, which needs
 * `window`.
 */

/** The single way past the limit this plan has. */
export type AppLimitRoute = 'fee' | 'upgrade';

/** Which action the limit interrupted; decides the copy's verb. */
export type AppLimitSurface = 'publish' | 'add';

/**
 * Whether the workspace has run out of room for one more app. Only `studio`
 * workspaces carry an allowance: Classic and unsubscribed ones are exempt, and an
 * unresolved read fails OPEN, because wrongly blocking is far worse than wrongly
 * allowing — the server enforces the same allowance regardless.
 *
 * Both surfaces that can reach the limit ask exactly this, because neither's app
 * is in `countableApps` yet: a marketplace app being added does not exist, and a
 * build is discounted from the server's count until it publishes. Building is
 * never gated — an app is charged against the allowance when it goes live.
 */
export const isUsageAtLimit = (
  usage: StudioAppUsageResponse | undefined,
): boolean =>
  usage?.family === 'studio' &&
  !usage.unlimitedApps &&
  usage.countableApps >= usage.includedApps;

/**
 * Whether a Freemium workspace is already over its allowance — not merely at
 * it. Distinct from {@link isUsageAtLimit} (`>=`), which intercepts a new add.
 *
 * At-limit (exactly 3/3) is compliant Freemium usage and must not lock
 * already-installed extensions. Over-limit is reachable when
 * `EnableStudioPricing` flips on for a portal that accumulated apps while the
 * flag was off. Paid Studio plans buy extras past the cap, so over-count there
 * is not a lock. Classic / unresolved (`studioPlan` null) carry no Freemium
 * allowance.
 */
export const isFreemiumOverAppLimit = (
  studioPlan: StudioPlanId | null,
  countableApps: number,
  includedApps: number,
): boolean => studioPlan === 'freemium' && countableApps > includedApps;

/**
 * Already-installed extensions lock only when Freemium is over the cap and the
 * item is appId-backed. Embeds/links (no appId) never count and must stay usable.
 */
export const isInstalledExtensionOverLimit = (
  appId: string | undefined,
  studioPlan: StudioPlanId | null,
  countableApps: number,
  includedApps: number,
): boolean =>
  Boolean(appId) &&
  isFreemiumOverAppLimit(studioPlan, countableApps, includedApps);

/**
 * The plan the paywall's picker opens on. Free is pitched Pro rather than the
 * cheapest paid plan, as the design preselects it.
 */
export const DEFAULT_UPGRADE_PLAN: SellableStudioPlanId = 'professional';

/**
 * What a paid plan includes, quoted in the Free paywall's pitch. The one
 * allowance the server reports is the plan the workspace is already on, and the
 * pitch is for a plan it is not on yet — so this number has no read behind it
 * and mirrors the backend's `StudioIncludedApps`.
 */
export const PAID_PLAN_INCLUDED_APPS = 5;

/** A paid plan the paywall can sell, with the tier its price comes from. */
export interface AppLimitPlanOption {
  planId: SellableStudioPlanId;
  tier: StudioPlanTier;
}

/**
 * The plans the paywall's picker offers, cheapest first. Derived from the
 * catalog rather than a list of its own, so the picker can only ever offer a
 * plan Stripe actually sells at a price it actually quoted.
 */
export const getUpgradePlanOptions = (
  plans: StudioPlan[] | undefined,
): AppLimitPlanOption[] =>
  (plans ?? [])
    .flatMap((plan) => {
      const tier = plan.tiers.at(0);
      // Free is what the workspace is already on, and a plan the catalog
      // carries no tier for has no price to show. Enterprise never comes
      // back from the catalog; the check is what narrows to the sellable set.
      if (plan.plan === 'freemium' || plan.plan === 'enterprise' || !tier) {
        return [];
      }
      return [{ planId: plan.plan, tier }];
    })
    .sort((a, b) => getStudioPlanRank(a.planId) - getStudioPlanRank(b.planId));
